import { useEffect, useState, useRef } from 'react';

function slugifyBase(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-_]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function TableOfContent({
    contentRef,
    selector = "h2, h3"
}) {
    const [items, setItems] = useState([]);
    const [activeId, setActiveId] = useState("");
    const observerRef = useRef(null);

    // Scan headings using a MutationObserver so we catch
    // headings rendered asynchronously by <Markdown>.
    useEffect(() => {
        const root = contentRef?.current;
        if (!root) return;

        const scan = () => {
            const headings = Array.from(root.querySelectorAll(selector));
            if (headings.length === 0) return;

            const usedIds = new Set();
            const slugCounts = new Map();

            const nextIdFor = (base) => {
                const normalized = base || "section";
                const current = slugCounts.get(normalized) ?? 0;
                const next = current + 1;
                slugCounts.set(normalized, next);
                return next === 1 ? normalized : `${normalized}-${next}`;
            };

            const tocItems = headings.map((el) => {
                const level = el.tagName === "H3" ? 3 : 2;
                const text = (el.textContent || "").trim();

                let id = el.getAttribute("id");
                if (!id) {
                    const base = slugifyBase(text);
                    id = nextIdFor(base);
                    while (usedIds.has(id)) id = nextIdFor(base);
                    el.setAttribute("id", id);
                }

                usedIds.add(id);
                return { id, text, level };
            });

            setItems(tocItems);
        };

        // Try immediately in case content is already rendered
        scan();

        // Watch for new child elements being added (Markdown async render)
        const mutationObserver = new MutationObserver(() => scan());
        mutationObserver.observe(root, { childList: true, subtree: true });

        return () => mutationObserver.disconnect();
    }, [contentRef, selector]);

    // Observe heading visibility with IntersectionObserver
    // to track the active (visible) heading.
    useEffect(() => {
        if (items.length === 0) return;

        const handleIntersect = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveId(entry.target.id);
                }
            });
        };

        observerRef.current = new IntersectionObserver(handleIntersect, {
            rootMargin: "-20% 0% -35% 0px",
        });

        items.forEach((item) => {
            const el = document.getElementById(item.id);
            if (el) observerRef.current.observe(el);
        });

        return () => observerRef.current?.disconnect();
    }, [items]);

    const scrollToId = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <nav className="article-toc-nav">
            <ul className="article-toc-list">
                {items.map((item) => {
                    const levelClass = item.level === 3 ? "article-toc-item is-h3" : "article-toc-item";
                    const activeClass = item.id === activeId ? " is-active" : "";
                    return (
                        <li
                            key={item.id}
                            className={levelClass + activeClass}
                        >
                            <button
                                type="button"
                                className="article-toc-link"
                                onClick={() => scrollToId(item.id)}
                            >
                                {item.text}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}

export default TableOfContent;
